import React, { useState, useEffect } from 'react';
import InfluencerDashboardPage from '../components/InfluencerDashboardPage';
import { Search, BarChart3, Package, Chrome, Zap, ShoppingCart, ChevronLeft, ChevronRight, Smartphone, Lock, Info, Calendar, Store, Trophy, Sparkles } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import UngatedBrandsWidget from '../components/UngatedBrandsWidget';
import SplitHero from '../components/SplitHero';
import authService from '../services/AuthService';
import BuylistViewerModal from '../components/BuylistViewerModal';
import StorefrontSourcingModal from '../components/StorefrontSourcingModal';
import MspGptBanner from '../components/MspGptBanner';

/* ── VideoTooltip ── */
const VideoTooltip = ({ videoUrl }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showVideo, setShowVideo] = useState(false);

    const handleClick = (e) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
        if (!isOpen) setTimeout(() => setShowVideo(true), 300);
        else setShowVideo(false);
    };

    const handleClose = (e) => {
        e.stopPropagation();
        setIsOpen(false);
        setShowVideo(false);
    };

    return (
        <>
            <button
                onClick={handleClick}
                className="ml-1 w-20 h-5 mt-1 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-colors cursor-pointer shadow-md hover:shadow-lg"
                title="Watch video"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className='-ml-2'>
                    <circle cx="12" cy="12" r="10" fill="#ff000058" />
                    <polygon points="9,7 9,17 17,12" fill="white" />
                </svg>
                <span className='text-[12px]'>Tutorial</span>
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 animate-fadeIn" onClick={handleClose} />
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <div className="pointer-events-auto bg-gray-900 rounded-xl shadow-2xl overflow-hidden border-2 border-red-600 animate-fadeIn max-w-2xl w-full">
                            <div className="relative">
                                {showVideo ? (
                                    <iframe
                                        width="100%" height="400"
                                        src={`${videoUrl}?controls=1&modestbranding=1`}
                                        title="Feature explanation"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                        allowFullScreen className="block"
                                    />
                                ) : (
                                    <div className="w-full h-96 bg-gray-800 flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                                    </div>
                                )}
                                <button
                                    onClick={handleClose}
                                    className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

/* ── DescriptionTooltip ── */
const DescriptionTooltip = ({ description }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="relative inline-block">
            <button
                onMouseEnter={() => setIsOpen(true)}
                onMouseLeave={() => setIsOpen(false)}
                onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
                className="p-3 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition flex items-center gap-2 text-sm font-semibold"
            >
                <Info size={16} />
                What's this?
            </button>
            {isOpen && (
                <div className="absolute left-0 mt-2 w-80 bg-gray-900 text-white p-4 rounded-lg shadow-2xl z-20 animate-fadeIn">
                    <p className="text-sm leading-relaxed">{description}</p>
                    <div className="absolute -top-2 left-6 w-4 h-4 bg-gray-900 transform rotate-45"></div>
                </div>
            )}
        </div>
    );
};

/* ── ToolCard ── */
const ToolCard = ({ icon: Icon, title, description, comingSoon, platforms, tags, tagsExt = false, onClick, locked = false, requiredPlan = '', onUpgrade, videoUrl = "https://www.youtube.com/embed/dQw4w9WgXcQ", marketplacesSellOn = [] }) => {
    return (
        <div
            onClick={locked ? undefined : onClick}
            className={`bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition border-2 border-transparent ${!locked && 'hover:border-teal-500'} ${!locked && onClick ? 'cursor-pointer' : 'cursor-default'} relative`}
        >
            <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="text-white" size={24} />
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-1">
                            {title}
                            <VideoTooltip videoUrl={videoUrl} />
                        </h3>
                        {platforms && platforms.length > 0 && (
                            <div className="flex items-center gap-4 ml-auto">
                                {platforms.map((platform, idx) => {
                                    const isDisabled = marketplacesSellOn === null ? false : !marketplacesSellOn.includes(platform) && platform !== "aip";
                                    return (
                                        <div key={idx} className="relative group">
                                            <img
                                                src={`/icons/${platform}.png`}
                                                alt={platform}
                                                className="h-7 w-8 object-contain rounded"
                                                style={{ opacity: isDisabled ? 0.4 : 1, marginTop: platform === "amazon" && "2px" }}
                                                title={platform.charAt(0).toUpperCase() + platform.slice(1)}
                                            />
                                            {isDisabled && (
                                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                    <span className="text-red-500 text-lg font-bold leading-none">✕</span>
                                                </div>
                                            )}
                                            {isDisabled && (
                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center">
                                                    You haven't selected <span className="capitalize font-semibold">{platform}</span> in your settings.{' '}
                                                    <a href="/settings" className="underline text-teal-300 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                                                        Go to Settings
                                                    </a>{' '}to enable it.
                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {locked ? (
                        <div className="space-y-3">
                            <p className="flex items-center gap-2 text-gray-700 font-semibold text-sm">
                                <Lock className="text-gray-400" size={20} />
                                MSP {requiredPlan} Plan Required
                            </p>
                            <div className="flex gap-3 mt-4">
                                <button
                                    onClick={onUpgrade}
                                    className="px-6 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition shadow-lg"
                                >
                                    Upgrade to Unlock
                                </button>
                                <DescriptionTooltip description={description} />
                            </div>
                        </div>
                    ) : (
                        <>
                            <p className="text-gray-600 leading-relaxed mb-3">{description}</p>
                            {tags && tags.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-auto">
                                    {tagsExt ? (
                                        tags.map((tag, idx) => (
                                            <span key={idx} className="text-xs bg-green-200 text-black border-green-400 font-bold shadow-xl border px-2 py-1 rounded-md">{tag}</span>
                                        ))
                                    ) : (
                                        <span className="text-xs text-gray-600">{tags.join(' • ')}</span>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ── Seller Dashboard (your original DashboardPage content) ── */
const SellerDashboard = ({ user }) => {
    const navigate = useNavigate();
    const isNonUS = user?.amazon_marketplace && user.amazon_marketplace !== 'US';

    const currentPlan = user?.subscription_plan || 'No plan';
    const isSubscriptionActive = user?.subscription_active || false;

    const isPlanUnlocked = (requiredPlan) => {
        if (!isSubscriptionActive) return false;
        if (currentPlan === 'No plan') return false;
        if (currentPlan.startsWith('MIP_')) return false; // MIP-only users get nothing here
        const planHierarchy = {
            'Beginner': 1, 'Pro': 2, 'Unlimited': 3,         // bundle
            'MSP_Beginner': 1, 'MSP_Pro': 2, 'MSP_Unlimited': 3,  // msp only
        };
        return planHierarchy[currentPlan] >= planHierarchy[requiredPlan];
    };

    const handleUpgrade = () => navigate('/pricing');

    const handleQuickSearch = () => {
        const trimmed = quickSearchInput.trim();
        if (!trimmed) return;
        setQuickSearchQuery(trimmed);
    };
    const [buylistViewerOpen, setBuylistViewerOpen] = useState(false);
    const [storefrontSourcingOpen, setStorefrontSourcingOpen] = useState(false);
    const [quickSearchInput, setQuickSearchInput] = useState('');
    const [quickSearchQuery, setQuickSearchQuery] = useState(null); // set on submit, opens modal

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour >= 6 && hour < 12) return "Coffee and Sourcing?";
        if (hour >= 12 && hour < 17) return "Let's find some leads.";
        if (hour >= 17 && hour < 21) return "One more search before dinner?";
        return "Running a search before bed?";
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(5px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
                .blur-section { filter: blur(4px); pointer-events: none; user-select: none; }

                @keyframes pulse-glow {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(251, 146, 60, 0.7); }
                    50% { box-shadow: 0 0 20px 8px rgba(251, 146, 60, 0.5); }
                }

                @media (max-width: 640px) {
                    .mobile-app-btn {
                        background: linear-gradient(to right, #fbbf24, #f97316);
                        color: white;
                        border-color: #f59e0b;
                        animation: pulse-glow 1.8s ease-in-out infinite;
                    }
                }
            `}</style>

            <div className="fixed inset-0 opacity-10 pointer-events-none">
                <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl" style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
                <div className="absolute top-0 -right-4 w-96 h-96 rounded-full filter blur-3xl" style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
                <div className="absolute -bottom-8 left-20 w-96 h-96 rounded-full filter blur-3xl" style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
            </div>

            <div className="max-w-7xl mx-auto">
                <div className="mb-9">
                    <div className='flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6'>
                        <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-0 sm:mb-2">{getGreeting()}</h1>
                        <div className="mt-0 sm:mt-1 flex flex-wrap items-center gap-2">
                            <span className={`inline-block ${currentPlan === 'No plan' || !isSubscriptionActive ? 'bg-gray-400' : 'bg-gradient-to-r from-teal-500 to-blue-600'} text-white px-4 py-2 rounded-full font-semibold text-sm`}>
                                Current Plan: {currentPlan.replace("_", " ")} {!isSubscriptionActive && currentPlan !== 'No plan' && '(Inactive)'}
                            </span>
                            {(currentPlan === 'No plan' || !isSubscriptionActive) && (
                                <button onClick={handleUpgrade} className="px-4 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-full font-semibold text-sm hover:opacity-90 transition shadow-lg">
                                    {currentPlan === 'No plan' ? 'Get Started' : 'Reactivate'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mb-6 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
                    <div className="flex-1 min-w-0 bg-amber-100 border border-amber-300 rounded-lg px-3 py-3 sm:px-4 sm:py-2 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
                        <p className="text-amber-800 font-medium text-sm text-center sm:text-left">Looking for our amazing apps?</p>

                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                            <a href="/mobile-app" className="mobile-app-btn flex items-center justify-center gap-1.5 bg-amber-200 text-amber-900 px-3 py-2 sm:py-1 rounded-lg hover:bg-amber-300 transition text-sm font-medium border border-amber-300 w-full sm:w-auto">
                                <Smartphone size={14} />Mobile App
                            </a>

                            <a href="https://chromewebstore.google.com/detail/multisellerpro/elebkaoefjdagkbfienaoonkgifogkob" className="flex items-center justify-center gap-1.5 bg-amber-200 text-amber-900 px-3 py-2 sm:py-1 rounded-lg hover:bg-amber-300 transition text-sm font-medium border border-amber-300 w-full sm:w-auto" target="_blank" rel="noreferrer">
                                <Chrome size={14} />Chrome Extension
                            </a>
                        </div>

                        <div className="w-full h-px sm:w-px sm:h-5 bg-amber-400 opacity-50 sm:block" />

                        <a href="/webinars" className="flex items-center justify-center gap-2 bg-amber-600 text-amber-50 px-3 py-2 sm:py-1 rounded-lg hover:bg-amber-700 transition text-sm font-medium w-full sm:w-auto" target="_blank" rel="noreferrer">
                            <span className="w-2 h-2 rounded-full bg-amber-200 animate-pulse flex-shrink-0" />
                            <Calendar size={14} />Join Weekly Webinar
                        </a>
                    </div>

                    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm w-[20%]">
                        <Search size={16} className="text-slate-400 flex-shrink-0" />
                        <input
                            type="text"
                            value={quickSearchInput}
                            onChange={(e) => setQuickSearchInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleQuickSearch(); }}
                            placeholder="Quick product search..."
                            className="flex-1 min-w-0 text-sm outline-none bg-transparent"
                        />
                        <button
                            onClick={handleQuickSearch}
                            className="px-3 py-1.5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-md text-sm font-semibold hover:opacity-90 transition flex-shrink-0"
                        >
                            Search
                        </button>
                    </div>

                    <button
                        onClick={() => setBuylistViewerOpen(true)}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white px-4 py-3 sm:py-2 rounded-lg font-semibold text-sm hover:opacity-90 transition shadow-lg whitespace-nowrap flex-shrink-0 w-full sm:w-auto"
                    >
                        <ShoppingCart size={16} />
                        Buylist Viewer
                    </button>
                </div>

                {/* Ungated brands — Amazon sellers only */}
                {user?.marketplaces_sell_on?.map(m => m.toLowerCase()).includes('amazon') && (
                    <div className="mb-8">
                        <UngatedBrandsWidget user={user} hasCrawlerAccess={isPlanUnlocked(user?.amazon_marketplace === 'US' ? 'Pro' : 'Beginner')} />
                    </div>
                )}

                {isNonUS && (
                    <div className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                            <Chrome className="text-teal-600" size={32} />
                            Chrome Ext. & Mobile Apps
                        </h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <ToolCard icon={Chrome} title="MSP Chrome Extension" platforms={['amazon', 'walmart', 'ebay', 'aip']} description="Powerful browser extensions for on-the-go product analysis. Get instant data overlays while browsing ANYWHERE. Make sourcing decisions without leaving the page." tags={['NEW: Amazon Influencer Program']} tagsExt onClick={() => window.location.href = "https://chromewebstore.google.com/detail/multisellerpro/elebkaoefjdagkbfienaoonkgifogkob"} locked={!isPlanUnlocked('Beginner')} requiredPlan="Beginner" onUpgrade={handleUpgrade} marketplacesSellOn={null} videoUrl="https://www.youtube.com/embed/RkcCIAh6-PA" />
                            <ToolCard icon={Chrome} title="Ungate Machine" platforms={['amazon']} description="Submit a list of ASINs and we'll automatically attempt to ungate each one for you. Get instant approval status and a complete summary of what got ungated!" locked={!isPlanUnlocked('Beginner')} onClick={() => window.location.href = "https://chromewebstore.google.com/detail/msp-ungate-machine/figkjlhilchcoefjkbmldfoocplcalof"} requiredPlan="Beginner" onUpgrade={handleUpgrade} marketplacesSellOn={null} videoUrl="https://www.youtube.com/embed/BFABuuhvw9E" />
                            <ToolCard icon={Smartphone} title="RA Mobile App" platforms={['amazon', 'walmart', 'ebay', 'aip']} description="Powerful Mobile App. Retail arbitrage, Flipping, everything you need for RA is here." locked={!isPlanUnlocked('Beginner')} onClick={() => window.location.href = "/mobile-app"} requiredPlan="Beginner" onUpgrade={handleUpgrade} marketplacesSellOn={null} videoUrl="https://www.youtube.com/embed/RkcCIAh6-PA" />
                        </div>
                    </div>
                )}

                <div className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <Search className="text-teal-600" size={32} />
                        Sourcing Tools
                    </h2>
                    <MspGptBanner tool="seller" locked={!isPlanUnlocked('Pro')} onUpgrade={handleUpgrade} />
                    <div className="grid md:grid-cols-2 gap-6">
                        {!isNonUS && <ToolCard icon={Zap} title="Universal Live Scan" platforms={['amazon', 'walmart', 'ebay']} description="Scan products in real-time across online retailers. Get instant profit calculations, sales estimates, competition analysis, and gated status. Run the Universal Scan on any website and we'll do everything for you!" tags={['Real-time', 'Multi-platform', 'Ungated Finder']} onClick={() => navigate('/universal-live-scan')} locked={!isPlanUnlocked('Pro')} requiredPlan="Pro" onUpgrade={handleUpgrade} marketplacesSellOn={user?.marketplaces_sell_on?.map(mp => mp.toLowerCase()) || []} videoUrl="https://www.youtube.com/embed/rtlDOpvow3s" />}
                        {!isNonUS && <ToolCard icon={Search} title="Reverse Scan" platforms={['amazon', 'walmart', 'ebay']} description="Find profitable deals by reverse searching from a list of ASINs / Item IDs / Keepa Search. We'll tell you where to buy them from, show you all the options, and constantly update your gated status." tags={['ASIN Search', 'Keepa Product Finder', 'Ungated Finder']} onClick={() => navigate('/reverse-scan')} locked={!isPlanUnlocked('Pro')} requiredPlan="Pro" onUpgrade={handleUpgrade} marketplacesSellOn={user?.marketplaces_sell_on?.map(mp => mp.toLowerCase()) || []} videoUrl="https://www.youtube.com/embed/taKWlLvPBUI" />}
                        <ToolCard icon={ShoppingCart} title="Storefront Scan" platforms={['amazon', 'walmart', 'ebay']} description="Analyze entire competitor storefronts to identify their best-selling products. We'll find where you can buy them from so you can sell them yourself!" tags={['Storefront Stalking', 'Daily Update', 'Ungated Finder']} onClick={() => navigate('/storefront-scan')} locked={!isPlanUnlocked('Pro')} requiredPlan="Pro" onUpgrade={handleUpgrade} marketplacesSellOn={user?.marketplaces_sell_on?.map(mp => mp.toLowerCase()) || []} videoUrl="https://www.youtube.com/embed/at8bxgKZdYE" />
                        <ToolCard icon={Store} title="Storefront Sourcing" platforms={['amazon', 'walmart']} description="Find sellers worth stalking based on your criteria - helps surface more ungated and profitable leads - then bulk-source and stalk their storefronts." tags={['Seller Finder', 'Amazon + Walmart']} onClick={() => setStorefrontSourcingOpen(true)} locked={!isPlanUnlocked('Pro')} requiredPlan="Pro" onUpgrade={handleUpgrade} marketplacesSellOn={user?.marketplaces_sell_on?.map(mp => mp.toLowerCase()) || []} videoUrl="https://www.youtube.com/embed/aBNqUV09LJU?t=1260" />
                        <ToolCard icon={Package} title="Wholesale Scan" platforms={['amazon', 'walmart', 'ebay']} description="Bulk scan wholesale catalogs and supplier lists. Quickly identify profitable products with high demand and low competition. Perfect for scaling your business with wholesale sourcing." tags={['CSV File', 'Wholesale', 'Ungated Finder']} onClick={() => navigate('/wholesale')} locked={!isPlanUnlocked('Pro')} requiredPlan="Pro" onUpgrade={handleUpgrade} marketplacesSellOn={user?.marketplaces_sell_on?.map(mp => mp.toLowerCase()) || []} videoUrl="https://www.youtube.com/embed/-bp1tvKplD0" />
                        {!isNonUS && <ToolCard icon={BarChart3} title="Amazon to Amazon (A2A)" platforms={['amazon']} description="Buy from Amazon and sell on Amazon. We'll show you products with price drops large enough to flip them later and profit!" tags={['Amazon Flip', 'Price Drops', 'A2A']} onClick={() => navigate('/a2a-deals')} locked={!isPlanUnlocked('Pro')} requiredPlan="Pro" onUpgrade={handleUpgrade} marketplacesSellOn={user?.marketplaces_sell_on?.map(mp => mp.toLowerCase()) || []} videoUrl="https://www.youtube.com/embed/JWLRxRK_Acw" />}
                        {!isNonUS && <ToolCard icon={BarChart3} title="Amazon to Walmart (A2W)" platforms={['walmart']} description="Buy from Amazon, sell on Walmart. High Profit & ROI." tags={['Amazon to Walmart', 'Price Drops', 'A2W']} onClick={() => navigate('/a2w')} locked={!isPlanUnlocked('Pro')} requiredPlan="Pro" onUpgrade={handleUpgrade} marketplacesSellOn={user?.marketplaces_sell_on?.map(mp => mp.toLowerCase()) || []} videoUrl="https://www.youtube.com/embed/fZ3Gql0NqgI" />}
                        <ToolCard icon={Trophy} title="How I Use MSP" platforms={['amazon', 'walmart', 'ebay']} description="Live Sourcing, Tutorials, Guides - On this page, you'll learn how to use MSP at its full power, find profitable leads, and improve your workflow." onClick={() => navigate('/how-i-use-msp')} locked={!isPlanUnlocked('Pro')} requiredPlan="Pro" onUpgrade={handleUpgrade} marketplacesSellOn={user?.marketplaces_sell_on?.map(mp => mp.toLowerCase()) || []} videoUrl="https://www.youtube.com/embed/1xpm8sUrac4" />
                    </div>
                </div>

                {!isNonUS && <div className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <Chrome className="text-teal-600" size={32} />
                        Chrome Ext. & Mobile Apps
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <ToolCard icon={Chrome} title="MSP Chrome Extension" platforms={['amazon', 'walmart', 'ebay', 'aip']} description="Powerful browser extensions for on-the-go product analysis. Get instant data overlays while browsing ANYWHERE. Make sourcing decisions without leaving the page." tags={['NEW: Amazon Influencer Program']} tagsExt onClick={() => window.location.href = "https://chromewebstore.google.com/detail/multisellerpro/elebkaoefjdagkbfienaoonkgifogkob"} locked={!isPlanUnlocked('Beginner')} requiredPlan="Beginner" onUpgrade={handleUpgrade} marketplacesSellOn={null} videoUrl="https://www.youtube.com/embed/RkcCIAh6-PA" />
                        <ToolCard icon={Chrome} title="Ungate Machine" platforms={['amazon']} description="Submit a list of ASINs and we'll automatically attempt to ungate each one for you. Get instant approval status and a complete summary of what got ungated!" locked={!isPlanUnlocked('Beginner')} onClick={() => window.location.href = "https://chromewebstore.google.com/detail/msp-ungate-machine/figkjlhilchcoefjkbmldfoocplcalof"} requiredPlan="Beginner" onUpgrade={handleUpgrade} marketplacesSellOn={null} videoUrl="https://www.youtube.com/embed/BFABuuhvw9E" />
                        <ToolCard icon={Smartphone} title="RA Mobile App" platforms={['amazon', 'walmart', 'ebay', 'aip']} description="Powerful Mobile App. Retail arbitrage, Flipping, everything you need for RA is here." locked={!isPlanUnlocked('Beginner')} onClick={() => window.location.href = "/mobile-app"} requiredPlan="Beginner" onUpgrade={handleUpgrade} marketplacesSellOn={null} videoUrl="https://www.youtube.com/embed/RkcCIAh6-PA" />
                        <ToolCard icon={Smartphone} title="MSP Local Scan" description="Enter UPC and Zip code and get info about local stock / inventory around you!" onClick={() => window.location.href = "https://app.multisellerpro.com/local-scan"} locked={!isPlanUnlocked('Beginner')} requiredPlan="Beginner" onUpgrade={handleUpgrade} marketplacesSellOn={null} videoUrl="https://www.youtube.com/embed/RwE7Kpis9pU" />
                    </div>
                </div>}

                {!isNonUS && <div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <Package className="text-teal-600" size={32} />
                        Upcoming Tools - Must Wait
                    </h2>
                    <div className="bg-white rounded-xl shadow-2xl p-8 border-2 border-purple-400 relative">
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-600 text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg z-30">
                            Coming Very Soon
                        </div>
                        <div className="blur-section mt-6">
                            <div className="flex items-start gap-4 mb-6">
                                <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <BarChart3 className="text-white" size={24} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold text-gray-800 mb-2">Mystery Tool #1 - You Won't Believe It</h3>
                                    <p className="text-gray-600 leading-relaxed">This tool is so powerful, even our developers are scared to release it. Legend says it was found in Area 51.</p>
                                </div>
                            </div>
                            <div className="space-y-3 mt-6">
                                {['Your Coffee Will Never Get Cold Again ☕', 'Teleportation Button (Warehouse to Couch) 🛋️', 'Jeff Bezos Personal Phone Number* 📞', 'UNO! Auto-Deny Returns 🔄', 'One-Click "Make Money" Button** 💰', 'Pet Your Dog Remotely Feature 🐕'].map((feature, idx) => (
                                    <div key={idx} className="flex items-start gap-3">
                                        <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ color: 'rgba(50, 121, 214, 1)' }}>
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                                        </div>
                                        <span className="text-gray-700 text-sm">{feature}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 text-xs text-gray-500 space-y-1">
                                <p>* Number may or may not connect to Jeff. Probably connects to Gary from accounting.</p>
                                <p>** Button requires subscription, WiFi, luck, and a four-leaf clover.</p>
                            </div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/80 rounded-xl">
                            <div className="text-center p-6">
                                <div className="mb-3"><span className="text-5xl">🚀</span></div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Game-Changing Tools Loading...</h3>
                                <p className="text-gray-700 text-sm leading-relaxed mb-3"><span className="font-bold text-purple-600">Lock in MSP Unlimited NOW!</span></p>
                                <p className="text-gray-600 text-xs mb-2">Early subscribers get <span className="font-bold">lifetime pricing</span>.<br />Price increases after these tools launch!</p>
                                <p className="text-blue-600 text-xs font-semibold mb-4">These tools will revolutionize your entire operation</p>
                                <button onClick={handleUpgrade} className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg font-semibold hover:opacity-90 transition shadow-lg">Lock In Price Now</button>
                            </div>
                        </div>
                    </div>
                </div>}

                <BuylistViewerModal
                    isOpen={buylistViewerOpen}
                    onClose={() => setBuylistViewerOpen(false)}
                    authService={authService}
                />

                {storefrontSourcingOpen && (
                    <StorefrontSourcingModal onClose={() => setStorefrontSourcingOpen(false)} />
                )}

                {quickSearchQuery && (
                    <div
                        style={{
                            position: 'fixed', inset: 0, zIndex: 10000,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            backdropFilter: 'blur(4px)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: '16px',
                        }}
                        onClick={() => setQuickSearchQuery(null)}
                    >
                        <div
                            style={{
                                position: 'relative',
                                width: '45%',
                                height: '90%',
                                marginTop: "70px",
                                borderRadius: '16px',
                                overflow: 'hidden',
                                boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
                                backgroundColor: '#0f172a',
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <iframe
                                src={`https://app.multisellerpro.com/camera.html?search=${encodeURIComponent(quickSearchQuery)}`}
                                title="Quick Search"
                                style={{
                                    width: '100%',
                                    height: 'calc(100% + 14%)',
                                    marginTop: '-14%',
                                    border: 'none',
                                    display: 'block',
                                    zoom: 0.85,
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const DashboardPage = ({ user }) => {
    const isNonUS = user?.amazon_marketplace && user.amazon_marketplace !== 'US';

    const [activeTab, setActiveTab] = useState(() => {
        const stored = localStorage.getItem('preferredTool');
        return stored === 'influencer' ? 'influencer' : 'seller';
    });

    const [showChoiceModal, setShowChoiceModal] = useState(
        !isNonUS && !localStorage.getItem('preferredTool')
    );

    const location = useLocation();
    useEffect(() => {
        if (location.state?.tab) {
            setActiveTab(location.state.tab);
            setShowChoiceModal(false);
        }
    }, [location.key]);

    const handleChoose = (choice) => {
        localStorage.setItem('preferredTool', choice);
        setActiveTab(choice);
        setShowChoiceModal(false);
    };

    const handleCloseChoiceModal = () => {
        localStorage.setItem('preferredTool', 'seller');
        setActiveTab('seller');
        setShowChoiceModal(false);
    };

    if (isNonUS) {
        return <SellerDashboard user={user} />;
    }

    if (showChoiceModal) {
        return <SplitHero onChoose={handleChoose} onClose={handleCloseChoiceModal} />;
    }

    return (
        <div style={{
            position: 'sticky',
            top: 0,
            zIndex: 40,
            background: 'white',
            borderBottom: '1px solid #e2e8f0',
            boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
        }}>
            <div style={{
                maxWidth: '80rem',
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
            }}>
                {/* Seller Tab */}
                <button
                    onClick={() => { setActiveTab('seller'); localStorage.setItem('preferredTool', 'seller'); }}
                    style={{
                        padding: '14px 24px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 500,
                        color: activeTab === 'seller' ? '#0d9488' : '#94a3b8',
                        borderBottom: activeTab === 'seller' ? '2px solid #0d9488' : '2px solid transparent',
                        transition: 'color 0.2s, border-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke={activeTab === 'seller' ? '#0d9488' : '#94a3b8'}
                        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <path d="M16 10a4 4 0 01-8 0" />
                    </svg>
                    Seller Pro
                </button>

                {/* Influencer Tab */}
                <button
                    onClick={() => { setActiveTab('influencer'); localStorage.setItem('preferredTool', 'influencer'); }}
                    style={{
                        padding: '14px 24px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 500,
                        color: activeTab === 'influencer' ? '#2563eb' : '#94a3b8',
                        borderBottom: activeTab === 'influencer' ? '2px solid #2563eb' : '2px solid transparent',
                        transition: 'color 0.2s, border-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke={activeTab === 'influencer' ? '#2563eb' : '#94a3b8'}
                        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 7l-7 5 7 5V7z" />
                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                    Influencer Pro
                </button>
            </div>

            {/* ── Dashboard Content ── */}
            {activeTab === 'seller'
                ? <SellerDashboard user={user} />
                : <InfluencerDashboardPage user={user} />
            }
        </div>
    );
};

export default DashboardPage;